import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import EducationDrawer from 'components/EducationDrawer';

const VideoContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#0F172A' : '#F3F4F6'};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
  position: relative;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 12px;
  }
`;

const FAQSection = styled.div`
  margin-top: 32px;
`;

const FAQTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#374151')};
  margin-bottom: 16px;
`;

const FAQItem = styled.div`
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E5E7EB'};
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.2)' : '#D1D5DB'};
  }
`;

const FAQHeader = styled.button`
  width: 100%;
  padding: 16px;
  background: none;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  text-align: left;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(0,0,0,0.02)'};
  }
`;

const FAQContent = styled.div`
  padding: 0 16px;
  max-height: ${(props) => (props.isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding-bottom: ${(props) => (props.isOpen ? '16px' : '0')};
`;

const FAQText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#6B7280')};
  margin: 0;
`;

const ChevronIcon = styled.span`
  display: inline-block;
  transition: transform 0.2s;
  transform: rotate(${(props) => (props.isOpen ? '180deg' : '0')});
`;

const CampaignGuideDrawer = ({ isOpen, onClose }) => {
  const [expandedItems, setExpandedItems] = useState({});

  const toggleItem = (key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const faqItems = [
    {
      key: 'credits',
      title: 'Understanding Credits',
      content:
        'Credits are used to send messages to your customers. Each SMS message uses 1 credit, while push notifications use 0.5 credits. You can purchase additional credits from your account settings.',
    },
    {
      key: 'sms-vs-push',
      title: 'SMS vs Push Notifications',
      content:
        'SMS messages are text messages sent directly to phone numbers and have higher open rates but cost more. Push notifications are sent through your app and are more cost-effective but require users to have your app installed and notifications enabled.',
    },
    {
      key: 'best-practices',
      title: 'Best Practices for Messaging',
      content:
        'Keep messages short and actionable. Include a clear call-to-action. Send messages during optimal times (Thu 6PM, Fri 4PM for events). Personalize when possible using customer variables.',
    },
    {
      key: 'scheduling',
      title: 'Scheduling Messages',
      content:
        'You can schedule messages to be sent at a future date and time. This is useful for event reminders or time-sensitive promotions. Simply toggle "Schedule for later" and select your desired send time.',
    },
    {
      key: 'segments',
      title: 'Customer Segments',
      content:
        'Segments allow you to target specific groups of customers based on their behavior, preferences, or demographics. This ensures your messages reach the most relevant audience.',
    },
    {
      key: 'analytics',
      title: 'Message Analytics',
      content:
        'Track the performance of your messages with detailed analytics including open rates, click-through rates, and conversion metrics. Use these insights to improve future campaigns.',
    },
  ];

  return (
    <EducationDrawer
      title="Message Campaign Guide"
      isOpen={isOpen}
      onClose={onClose}
      showHelpButton={false}
    >
      <VideoContainer>
        <iframe
          src="https://player.vimeo.com/video/1063764809?h=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Message Campaign Tutorial"
        />
      </VideoContainer>

      <FAQSection>
        <FAQTitle>What would you like to learn more about?</FAQTitle>
        {faqItems.map((item) => (
          <FAQItem key={item.key}>
            <FAQHeader onClick={() => toggleItem(item.key)}>
              {item.title}
              <ChevronIcon isOpen={expandedItems[item.key]}>
                <svg
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              </ChevronIcon>
            </FAQHeader>
            <FAQContent isOpen={expandedItems[item.key]}>
              <FAQText>{item.content}</FAQText>
            </FAQContent>
          </FAQItem>
        ))}
      </FAQSection>
    </EducationDrawer>
  );
};

CampaignGuideDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CampaignGuideDrawer;
